import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatTableDataSource } from '@angular/material/table';

import { translate, TranslocoService } from '@jsverse/transloco';
import { ReplaySubject } from 'rxjs';

import { filter, takeUntil } from 'rxjs/operators';

import { ChartDisplayOptions, WidgetDataSource } from 'app/models';
import { DownloadAsDialogResult } from 'app/modules/collection/components/download-as-dialog/download-as-dialog.component';
import { FilterService, WidgetQueryOptions } from 'app/modules/collection/services/filter.service';
import { DashboardViewComponent } from 'app/modules/dashboard/pages/dashboard-view/dashboard-view.component';
import { ActionItemCacheService } from 'app/modules/dashboard/services/action-item-cache.service';
import { ActionPlanCacheService } from 'app/modules/dashboard/services/action-plan-cache.service';
import { ComparisonHandlerService } from 'app/modules/dashboard/services/comparison-handler-service';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { SemantriaAccount } from 'app/providers/bridge-api/account.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import {
  Comment,
  CommentPoint,
  Comparison,
  Widget,
  WidgetUtility,
} from 'app/providers/bridge-api/dashboard.service';
import { ActionItem, ActionPlan } from 'app/providers/storage-api/actionplan.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { DocumentService } from 'app/providers/storage-api/document.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { InteractiveSmartSummaryService } from 'app/providers/storage-api/interactive-smart-summary.service';
import { ProjectService } from 'app/providers/storage-api/project.service';
import { ReportService } from 'app/providers/storage-api/report.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import { LoggerService } from 'app/services/logger.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { TimezoneService } from 'app/services/timezone.service';

import { WidgetLimiterService } from 'app/services/widget-limiter.service';
import { FilterUtils } from 'app/utils/filter.utils';
import { Utils } from 'app/utils/utils';
import { Constants } from '../../../../constants/constants';
import { ModalCloseType } from '../../../../enums';
import { MatchmakerHandler } from '../../../../providers/semantria-api/spote.service';
import { FieldBucket } from '../../../field/field-buckets/field-buckets.component';
import { ActionPlanDialogOptions } from '../action-plan-dialog/action-plan-dialog.component';
import { PredictiveDateRange } from '../predictive-widget-datepickers/predictive-widget-datepickers.component';
import { SmartSummaryDialogComponent } from '../smart-summary-dialog/smart-summary-dialog.component';
import { AbstractTableWidgetComponent } from '../widget/abstract-table-widget.component';
import { WidgetComponent } from '../widget/widget.component';
import { WidgetChartOptions } from '../widget-chart/widget-chart.component';
import { ImpactReport } from '../../services/chart-report';
import {
  COMMENT_POINT_ID_SEPARATOR,
  ImpactDataItem,
} from '../../services/chart-report/chart-report-impact/chart-report-impact';
import { PptxReport } from '../../services/pptx.service';
import { PredictiveWidgetDataService } from '../../services/predictive-widget-data.service';
import { AnalyticItemImpact, SmartSummaryService } from '../../services/smart-summary.service';
import { WidgetCommentsHandlerService } from '../../services/widget-comments-handler.service';
import {
  FieldSelectDialogResult,
  ImpactMetric,
} from './rating-field-selector-dialog/rating-field-selector-dialog.component';

export interface WidgetImpactOptions extends WidgetChartOptions {
  isSpecial: boolean;
  ratingField: string;
  ratingScale: string;
  customBuckets?: FieldBucket[];
  originalBuckets?: FieldBucket[];
  selectedBucketIndex?: number;
  bucketName?: string;
  customBucketsByField?: {
    [fieldName: string]: { buckets: FieldBucket[]; selectedBucketIndex: number };
  };
  lastRatingScale?: string;
  impactMetric?: ImpactMetric;
}

export const IMPACT_REPORT_DATE_FORMAT = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'";

function formatPointId(point: any, widget?: Widget<any>): string {
  if (widget?.payload?.type === 'focusAreas') {
    return point.label;
  }
  return `${point.item}${COMMENT_POINT_ID_SEPARATOR}${point.column}`;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-impact',
  templateUrl: './widget-impact.component.html',
  styleUrls: ['./widget-impact.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WidgetImpactComponent
  extends AbstractTableWidgetComponent<WidgetImpactOptions>
  implements OnInit, OnDestroy
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @ViewChild('focusAreaMenuTrigger') focusAreaMenuTrigger: MatMenuTrigger;

  public tableDataSource: MatTableDataSource<ImpactDataItem>;
  public displayedColumns: string[] = ['label', 'left'];
  public hasData = false;

  private impactReport: ImpactReport;

  private columnFilterType: 'left' | 'right';
  private analyticItems: string[] = [
    'documents',
    'topics',
    'entities',
    'phrases',
    'themes',
    'taxonomy',
  ];

  public readonly downloadFormats = ['pptx', 'image'];
  readonly DEFAULT_RATING_MODAL_WIDTH = '600px';

  hasActionPlanFeature: boolean;
  hasSmartSummaryFeature: boolean;

  tableHeader: string;
  defaultImpactHeader: string;
  rightColumnHeader: string;
  leftColumnHeader: string;
  groupFieldsLoading = true;

  ratingFieldName: string;
  ratingOptionsButtonText: string;
  showRatingFieldSelectorModal;
  ratingFieldSelectorData: {
    dataSource: WidgetDataSource;
    ratingFieldName: string;
    customBuckets: FieldBucket[];
    selectedBucketIndex: number;
    isBucketView: boolean;
    cancelButtonText: string;
    scoringStrategy?: string;
    customBucketsByField?: {
      [fieldName: string]: { buckets: FieldBucket[]; selectedBucketIndex: number };
    };
    impactMetric?: ImpactMetric;
  };

  showRatingOptionsModal;
  ratingOptionsData: ChartDisplayOptions;

  isActionPlanDialogOpen = false;
  actionPlans: ActionPlan[];
  actionPlanByRatingField: ActionPlan;
  actionPlanDialogOptions: ActionPlanDialogOptions;
  actionItems: ActionItem[] = [];
  canCreateActionItem: boolean;
  actionItemCreationTooltip: string;
  disablePinnedCommentClick = false;
  ratingTitle: string;
  ratingModalWidth = this.DEFAULT_RATING_MODAL_WIDTH;

  private unsub$ = new ReplaySubject<void>(1);

  constructor(
    private reportService: ReportService,
    private predictiveWidgetDataService: PredictiveWidgetDataService,
    private semantriaAccountService: SemantriaAccount,
    private comparisonService: ComparisonHandlerService,
    private smartSummaryService: SmartSummaryService,
    private translocoService: TranslocoService,
    private actionPlanCacheService: ActionPlanCacheService,
    private actionItemCacheService: ActionItemCacheService,
    protected filterService: FilterService,
    protected dialogService: DialogService,
    protected documentService: DocumentService,
    protected projectService: ProjectService,
    protected alertService: AlertService,
    protected widgetLimiterService: WidgetLimiterService,
    protected interactiveSmartSummaryService: InteractiveSmartSummaryService,
    protected dashboardViewComponent: DashboardViewComponent,
    protected widgetComponent: WidgetComponent,
    protected systemService: SystemService,
    protected storageAccountService: StorageAccountService,
    protected authService: AuthService,
    protected timezoneService: TimezoneService,
    protected customFieldService: CustomFieldsService,
    protected commentsHandlerService: WidgetCommentsHandlerService,
    private logger: LoggerService
  ) {
    super(
      filterService,
      dialogService,
      documentService,
      projectService,
      alertService,
      widgetLimiterService,
      interactiveSmartSummaryService,
      dashboardViewComponent,
      widgetComponent,
      systemService,
      storageAccountService,
      authService,
      timezoneService,
      customFieldService,
      commentsHandlerService
    );

    this.tableDataSource = new MatTableDataSource([]);
    this.canFilter = true;
    this.hasAnalyses = true;

    this.predictiveWidgetDataService.onTwtraDataSourceChanged
      .pipe(
        takeUntil(this.unsub$),
        filter(() => this.payload?.isSpecial)
      )
      .subscribe((ds) => {
        this.widgetComponent.changeDataSource(ds);
      });

    this.predictiveWidgetDataService.onSpecialImpactHideChanged
      .pipe(
        takeUntil(this.unsub$),
        filter((d) => d.id !== this.widget.id && this.payload?.isSpecial)
      )
      .subscribe((d) => {
        this.pointName = d.label;
        this.hideImpactItem();
      });

    this.predictiveWidgetDataService.onSpecialImpactAnalyticTypeChanged
      .pipe(
        takeUntil(this.unsub$),
        filter((d) => d.id !== this.widget.id && this.payload?.isSpecial)
      )
      .subscribe((d) => {
        this.tableHeader = this.updateTableHeader(
          d.analyticType.analyticItem,
          d.analyticType.customField
        );

        const needsRedraw = this.widgetComponent.setAnalyticTypeResult(d.analyticType);
        if (needsRedraw || d.analyticType.showBlanksChanged) {
          this.refresh().then(() => {
            this.widgetComponent.updateTemplateBooleans();
            this.widgetComponent.initWidgetActions();

            setTimeout(() => {
              this.widgetComponent.pillWrapper.checkCompactSize();
            });
          });
        }
      });
  }

  async ngOnInit(): Promise<void> {
    super.ngOnInit();

    this.hasActionPlanFeature = (await this.semantriaAccountService.sub.latest()).features.includes(
      'ACTION_PLANS'
    );

    this.hasSmartSummaryFeature =
      await this.smartSummaryService.hasPermissionForSummaryByDriverField();

    this.cdr.markForCheck();

    this.storageAccountService.groupFieldsSub.takeUntil(this.unsub$).subscribe(() => {
      this.groupFieldsLoading = false;
      this.tableHeader = this.updateTableHeader();
      this.ratingFieldName = Utils.truncate(
        FieldService.getFieldLongName(this.payload.ratingField)
      );
      this.cdr.markForCheck();
    });

    this.translocoService
      .selectTranslate('widgets.charting.impact')
      .pipe(takeUntil(this.unsub$))
      .subscribe((translation) => {
        this.defaultImpactHeader = translation;
        this.updateImpactHeaders();
        this.cdr.markForCheck();
      });

    this.setSpecialFields();

    this.ratingOptionsButtonText = this.updateScoringButtonText();

    this.impactReport = this.createImpactReport();

    this.actionPlanCacheService.list$.pipe(takeUntil(this.unsub$)).subscribe((l) => {
      this.actionPlans = l;
      this.setActionPlanByRatingField();
      this.cdr.markForCheck();
    });

    this.actionItemCacheService.list$
      .pipe(
        filter((v) => {
          if (!this.hasActionPlanByRatingField) {
            return false;
          }

          return this.actionPlanByRatingField.plan_id === v[0];
        }),
        takeUntil(this.unsub$)
      )
      .subscribe((v) => {
        this.actionItems = v[1];
        this.cdr.markForCheck();
      });

    this.listActionItems();

    this.refresh(true, true);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.unsub$.next();
    this.unsub$.complete();
  }

  private createImpactReport(): ImpactReport {
    return new ImpactReport(
      this.payload,
      this.reportService,
      this.filterService,
      this.comparisonService,
      this.timezoneService
    );
  }

  private updateImpactHeaders() {
    this.rightColumnHeader = this.defaultImpactHeader;

    if (this.comparison?.filters.length) {
      this.rightColumnHeader = this.comparison.filters[0].label;

      if (this.comparison.filters.length > 1) {
        this.leftColumnHeader = this.comparison.filters[1].label;
      }
    }
  }

  private updateTableHeader(
    analyticItem = this.widget.payload.analyticItem,
    isCustomField = this.widget.payload.customFieldSelected
  ) {
    if (isCustomField) {
      return FieldService.getFieldLongName(analyticItem);
    }
    return analyticItem;
  }

  private updateScoringButtonText() {
    const payload = this.widget.payload;
    let text = '';
    if (payload.scoringStrategy === 'BOX') {
      text = `${payload.boxDirection} ${payload.boxSize} ${payload.scoringStrategy} - ${payload.ratingScale}`;
    } else {
      payload.scoringStrategy
        ? (text = `${payload.scoringStrategy} - ${payload.ratingScale}`)
        : (text = '');
    }
    return text;
  }

  onShowRatingFieldSelectorModal(isBucketView = false) {
    const isCurrentlyBuckets = this.payload.impactMetric === ImpactMetric.BUCKETS;

    this.ratingFieldSelectorData = {
      dataSource: this.widgetDataSource,
      ratingFieldName: this.payload.ratingField,
      customBuckets: isCurrentlyBuckets ? this.payload.customBuckets : undefined,
      selectedBucketIndex: isCurrentlyBuckets ? this.payload.selectedBucketIndex : undefined,
      isBucketView,
      cancelButtonText: translate('widgets.options.cancel'),
      scoringStrategy: this.payload.scoringStrategy,
      customBucketsByField: this.payload.customBucketsByField,
      impactMetric: this.payload.impactMetric,
    };
    this.ratingTitle =
      this.widget.payload.type === 'focusAreas' ? 'select-field-focus-area' : 'select-field-impact';
    this.showRatingFieldSelectorModal = true;
  }

  onRatingFieldSelectorModalClosed($event: FieldSelectDialogResult | null | ModalCloseType) {
    this.showRatingFieldSelectorModal = false;

    if ($event && $event !== ModalCloseType.CLOSE && $event !== ModalCloseType.CANCEL) {
      const { field, buckets, selectedBucketIndex, impactMetric } = $event || {};
      this.ratingFieldName = field.label;
      this.payload.ratingField = field.fieldName;
      this.payload.originalBuckets = field.buckets;

      // Store the impact metric type on the payload
      this.payload.impactMetric = impactMetric;

      // Set default ratingScale based on impact metric type
      if (impactMetric === ImpactMetric.BUCKETS) {
        this.payload.customBuckets = buckets;
        this.payload.selectedBucketIndex = selectedBucketIndex;
        this.payload.bucketName = (buckets || field.buckets)?.[selectedBucketIndex]?.bucketName;

        // Set ratingScale to the range of the selected bucket
        const bucketsToUse = buckets || field.buckets;
        if (bucketsToUse && bucketsToUse.length > 0 && selectedBucketIndex !== undefined) {
          const selectedBucket = bucketsToUse[selectedBucketIndex];
          if (selectedBucket) {
            this.payload.ratingScale = `${selectedBucket.minimumValue}:${selectedBucket.maximumValue}`;
          }
        }

        // NPS and BOX are not compatible with BUCKETS metric - switch to AVERAGE
        if (this.payload.scoringStrategy === 'NPS' || this.payload.scoringStrategy === 'BOX') {
          this.payload.scoringStrategy = 'AVERAGE';
        }

        // Store custom buckets per field only when BUCKETS is selected
        if (buckets && selectedBucketIndex !== undefined) {
          // Preserve existing bucket data for other fields by creating a new object
          this.payload.customBucketsByField = {
            ...this.payload.customBucketsByField,
            [field.fieldName]: {
              buckets,
              selectedBucketIndex,
            },
          };
        }
        // Note: customBucketsByField is preserved by not being explicitly cleared here
      } else if (impactMetric === ImpactMetric.RATING) {
        // Clear bucket properties for RATING fields
        this.payload.customBuckets = undefined;
        this.payload.selectedBucketIndex = undefined;
        this.payload.bucketName = undefined;

        // Use last rating scale if available, otherwise default to 1-10
        this.payload.ratingScale = this.payload.lastRatingScale || '1:10';
      } else if (impactMetric === ImpactMetric.BINARY) {
        // Clear bucket properties for BINARY fields
        this.payload.customBuckets = undefined;
        this.payload.selectedBucketIndex = undefined;
        this.payload.bucketName = undefined;

        // Set rating scale to 0-100 for binary fields
        this.payload.ratingScale = '0:100';

        // BOX is not compatible with BINARY metric - switch to AVERAGE
        if (this.payload.scoringStrategy === 'BOX') {
          this.payload.scoringStrategy = 'AVERAGE';
        }
      }

      this.listActionItems();
      this.ratingOptionsButtonText = this.updateScoringButtonText();
      this.dashboardStore.markDirty();

      this.refresh();
    }
  }

  onShowRatingOptionsModal() {
    this.ratingOptionsData = {
      payload: JSON.parse(JSON.stringify(this.payload)),
      needRefresh: false,
      widgetDataSource: this.widgetDataSource,
      elementComponent: null,
      widgetGroup: this.widgetGroup,
      comparison: this.comparison,
      ratingFieldName: this.payload.ratingField,
    };
    this.showRatingOptionsModal = true;
  }

  onRatingOptionsModalClosed($event: ChartDisplayOptions) {
    this.showRatingOptionsModal = false;

    if ($event) {
      this.payload.ratingScale = $event.payload.ratingScale;
      this.payload.scoringStrategy = $event.payload.scoringStrategy;
      this.payload.boxDirection = $event.payload.boxDirection;
      this.payload.boxSize = $event.payload.boxSize;

      // Preserve customBucketsByField from the event payload
      if ($event.payload.customBucketsByField) {
        this.payload.customBucketsByField = $event.payload.customBucketsByField;
      }

      // Store the rating scale for future use only if current metric is RATING
      if (this.payload.impactMetric === ImpactMetric.RATING) {
        this.payload.lastRatingScale = $event.payload.ratingScale;
      }

      this.ratingOptionsButtonText = this.updateScoringButtonText();
      this.dashboardStore.markDirty();

      this.refresh();
    }
  }

  public getWidgetQueryOptions(
    filterType?: 'left' | 'right',
    explicitPointName?: string
  ): WidgetQueryOptions {
    let comparisonFilterSet = this.comparison?.filters[0];
    if (filterType === 'left') {
      comparisonFilterSet = this.comparison?.filters[1];
    }

    const widgetQueryOptions = this.filterService.getWidgetQueryOptions(
      this.widget,
      this.widgetDataSource,
      this.widgetGroup,
      comparisonFilterSet
    );

    widgetQueryOptions.pointName = explicitPointName ?? this.pointName;
    widgetQueryOptions.analyticItem = this.payload.analyticItem;
    widgetQueryOptions.analyticItems = this.analyticItems;

    // Add rating field and scale constraints for document filtering
    if (this.payload.ratingField && this.payload.ratingScale) {
      const [min, max] = this.payload.ratingScale.split(':').map(Number);

      // Use bucket scoring query options to constrain by the rating field range
      widgetQueryOptions.bucketScoringQueryOptions = {
        fieldId: this.payload.ratingField,
        minimumValue: min,
        maximumValue: max,
        includeMinimum: true,
        includeMaximum: true,
      };
    }

    return widgetQueryOptions;
  }

  get defaultTitle(): string {
    return WidgetUtility.getWidgetTitle(this.widget);
  }

  public canShowDataTable(): boolean {
    return false;
  }

  public isDocumentFilterGroupSelectEnabled(): boolean {
    return !this.widget.payload.isSpecial;
  }

  protected getDocumentsQuery(_initialQuery?: string): string {
    return this.filterService.getQueryForDocuments(
      this.getWidgetQueryOptions(this.columnFilterType)
    );
  }

  async showDocumentsForItem(areaItem?: { entity: string; entityDisplayName: string }) {
    const widgetQueryOptions = this.getWidgetQueryOptions(this.columnFilterType);
    if (areaItem) {
      widgetQueryOptions.pointName = areaItem.entity;
      widgetQueryOptions.customFieldSelected = false;
      widgetQueryOptions.standardFieldSelected = false;
    }
    const documentViewOptions = {
      entity: areaItem?.entity ?? this.pointName,
      entityDisplayName: areaItem?.entityDisplayName ?? this.pointDisplayName,
      analyticItem: this.payload.analyticItem,
    };

    this.getDocuments(widgetQueryOptions, documentViewOptions);
  }

  setSpecialFields() {
    this.canBeCloned = !this.payload.isSpecial;
    this.canBeDeleted = !this.payload.isSpecial;
    this.filterDisabled = this.payload.isSpecial;
    this.canFilter = !this.payload.isSpecial;

    this.widgetComponent.updateTemplateBooleans();
  }

  public onSortChanged($event) {
    this.payload.sort.order = $event.direction;
    this.payload.sort.field = $event.active;

    this.refresh();

    this.widgetComponent.updateTemplateBooleans();
  }

  public hideImpactItem() {
    this.hideItem({
      name: this.pointName,
      type: this.payload.analyticItem,
      displayName: this.pointDisplayName,
      subType: this.payload.entityTypeFilter,
    });

    this.predictiveWidgetDataService.changeSpecialImpactHide({
      id: this.widget.id,
      label: this.pointName,
    });
  }

  public analyticTypeSet(result: any): void {
    this.tableHeader = this.updateTableHeader(result.analyticItem, result.customField);

    if (this.payload.isSpecial) {
      this.predictiveWidgetDataService.changeSpecialImpactAnalyticType({
        id: this.widget.id,
        analyticType: result,
      });
    }
  }

  executeReportForCsv(_result: DownloadAsDialogResult) {
    // TODO implement when support CSV download
    throw new Error('Method not implemented.');
  }

  prepareDataForCsv(_data: any) {
    // TODO implement when support CSV download
    throw new Error('Method not implemented.');
  }

  async exportAsPptx(pptx: PptxReport): Promise<void> {
    this.addTitleSlideToPptx(pptx);

    const dataOptions = this.dataSlideOptions();
    const tableWidth = 12; // in inches
    let colW: any[] = this.tableDataSource.data.map(() => 1);
    const widthSum = colW.reduce((prev, curr) => prev + curr, 0);
    colW = colW.map((w) => ((w / widthSum) * tableWidth).toFixed(2));

    pptx.addTableSlide(
      this.widget.payload.type !== 'focusAreas'
        ? this.getPptTable()
        : this.generateFocusAreasTableForPptExport(),
      {
        newSlideStartY: 0.5,
        colW,
        w: tableWidth,
        h: 5.5,
        data: dataOptions,
        autoPage: true,
        lineWeight: -0.3,
        border: {
          color: 'black',
          pt: 2,
          type: 'solid',
        },
      }
    );
  }

  private generateFocusAreasTableForPptExport() {
    const column = [];

    this.tableDataSource.data.forEach((item: ImpactDataItem, index: number) => {
      const columnSeverity = {
        text: `${index + 1}|`,
        options: {
          fontSize: 60,
          valign: 'middle',
          align: 'center',
        },
      };
      const columnLabel = {
        text: item.labelDisplay,
        options: {
          fontSize: 32,
          valign: 'middle',
          align: 'center',
        },
      };
      const columnObj = {
        text: [columnSeverity, columnLabel],
        options: {
          valign: 'middle',
          align: 'center',
        },
      };

      column.push(columnObj);
    });

    return [column];
  }

  private dataSlideOptions() {
    const dataOptions = {};
    dataOptions['dataSource'] = this.widgetDataSource.displayName;
    dataOptions['docCount'] = this.filteredDocumentCount ?? this.widgetDataSource.documentCount;
    dataOptions['maxDocs'] = this.widgetDataSource.documentCount;
    return dataOptions;
  }

  private getPptTable() {
    const pptTable = {
      rows: [],
    };

    const header = this.tableHeader[0].toUpperCase().concat(this.tableHeader.slice(1));
    const staticHeaders = [{ text: header, options: { bold: true, fontSize: 14 } }];
    // For some reason this was not working: ...(condition && {object}) so we have to check this way :/
    if (this.leftColumnHeader) {
      staticHeaders.push({ text: this.leftColumnHeader, options: { bold: true, fontSize: 14 } });
    }
    if (this.rightColumnHeader) {
      staticHeaders.push({ text: this.rightColumnHeader, options: { bold: true, fontSize: 14 } });
    }
    const [displayedColsWithoutMenu, headers] = this.appendCommentColumnsAndHeaders(
      this.displayedColumns.filter((col) => col !== 'item-menu'),
      staticHeaders
    );
    pptTable.rows.push(headers);
    this.tableDataSource.data.forEach((row) => {
      const rowGroup = displayedColsWithoutMenu.map((col) => {
        switch (col) {
          case 'right':
          case 'left':
            const value = row[`${col}Score`];
            return {
              text: value ? Utils.round(value, 2) : translate('widgets.n-a'),
              options: { color: 'f0f8ff', fill: this.getTrendColor(value) },
            };
          case 'comment_left':
          case 'comment_right':
          case 'comment_label':
            const column = col.split('_')[1];
            const comment = this.getPinnedComment(this.getCellComments(row.label, column));
            let text = '';
            if (comment?.titlePinned) {
              text = `${comment.title}`;
            }
            if (comment?.summaryPinned) {
              text = `${text}\n${comment.summary}`;
            }
            return { text };
          default:
            return { text: row[col] };
        }
      });
      pptTable.rows.push(rowGroup);
    });
    return pptTable;
  }

  private getTrendColor(value: number) {
    if (value > 0.5) {
      return '0bb161';
    }
    if (value < -0.5) {
      return 'd11212';
    }
    return '949494';
  }

  private appendCommentColumnsAndHeaders(displayedColsWithoutMenu: string[], headers: any[]) {
    // Comments doesn't exists until we add a comment, if we delete them all the containing array stays
    if (this.widget.payload?.comments?.some((c) => c.summaryPinned || c.titlePinned)) {
      this.widget.payload.comments.forEach((comment) => {
        const targetCol = comment.target.pointId.split(COMMENT_POINT_ID_SEPARATOR)[1];
        if (
          displayedColsWithoutMenu.includes(targetCol) &&
          !displayedColsWithoutMenu.includes(`comment_${targetCol}`)
        ) {
          const startIndex = displayedColsWithoutMenu.indexOf(targetCol) + 1;
          displayedColsWithoutMenu.splice(startIndex, 0, `comment_${targetCol}`);
          headers.splice(startIndex, 0, {
            text: `${translate('widgets.comments.comments')} (${headers[startIndex - 1].text})`,
            options: { bold: true, fontSize: 14 },
          });
        }
      });
    }
    return [displayedColsWithoutMenu, headers];
  }

  public async refresh(
    reset = true,
    initialLoading = false,
    refreshedDatasource?: WidgetDataSource
  ): Promise<void> {
    super.refresh(reset, initialLoading, refreshedDatasource);

    if (!this.widgetDataSource || this.hasInvalidComparison) {
      this.loadingStarted.emit(initialLoading);
      this.loadingFinished.emit(initialLoading);
      this.isLoading = false;
      return;
    }

    this.displayedColumns = ['label', 'right'];

    if (this.comparison?.filters.length > 1) {
      this.displayedColumns = ['label', 'left', 'right', 'change'];
    }

    this.updateImpactHeaders();
    this.tableHeader = this.updateTableHeader();

    if (this.hasActionPlanFeature) {
      this.displayedColumns.push('item-menu');
    }

    this.isLoading = true;
    this.loadingStarted.emit(initialLoading);

    const getBucketsForParams = () => {
      const bucketsToUse = this.payload.customBuckets || this.payload.originalBuckets;

      return {
        fieldBuckets: JSON.stringify(bucketsToUse),
        bucketName: bucketsToUse?.[this.payload.selectedBucketIndex]?.bucketName,
      };
    };

    const params: any = {
      analysisName: this.widgetDataSource.analysisId,
      scoreMetric: this.widgetGroup?.parameters.scoreMetric ?? this.payload.ratingField,
      ratingScale: this.payload.ratingScale,
      scoringStrategy: this.payload.scoringStrategy,
      ...(this.payload.selectedBucketIndex !== undefined &&
        (this.payload.customBuckets || this.payload.originalBuckets) && {
          ...getBucketsForParams(),
        }),
      sortOrder: this.payload.sort.order,
      // First comparsion section filters
      comparisonSectionFilters: FilterUtils.comparisonBucketToSelectedSections(this.comparison, 0),
    };
    if (params.fieldBuckets) {
      delete params.ratingScale;
    }

    if (this.payload.scoringStrategy === 'BOX') {
      params.boxDirection = this.payload.boxDirection;
      params.boxSize = this.payload.boxSize;
    }

    const filterType = this.payload.sort.field as typeof this.columnFilterType;

    try {
      const firstQueryForDocuments = this.filterService.getQueryForDocuments(
        this.getWidgetQueryOptions(filterType),
        false,
        true
      );

      if (!this.impactReport) {
        this.impactReport = this.createImpactReport();
      }

      const firstResult = await this.impactReport.executeReportForImpact(
        params,
        this.widgetDataSource,
        firstQueryForDocuments
      );
      this.filteredDocumentCount = this.impactReport.lastDocumentCount;

      if (this.comparison?.filters.length > 1) {
        params.selectedItems = firstResult.data.map((d) => d.label);
        // Second comparsion section filters
        params.comparisonSectionFilters = FilterUtils.comparisonBucketToSelectedSections(
          this.comparison,
          1
        );
        const otherFilterType = filterType === 'left' ? 'right' : 'left';
        const secondQueryForDocuments = this.filterService.getQueryForDocuments(
          this.getWidgetQueryOptions(otherFilterType),
          false,
          true
        );

        const secondResult = await this.impactReport.executeReportForImpact(
          params,
          this.widgetDataSource,
          secondQueryForDocuments
        );
        this.filteredDocumentCount += this.impactReport.lastDocumentCount;

        for (const item of firstResult.data) {
          item.leftScore = item.rightScore;
          const secondData = secondResult.data.find((d) => d.label === item.label);
          item[`${otherFilterType}Score`] = secondData?.rightScore;
          if (item.leftScore != null && item.rightScore != null) {
            item.change = item.rightScore - item.leftScore;
          }
        }
      }

      this.tableDataSource.data =
        this.payload.type === 'focusAreas' ? firstResult.data.slice(0, 3) : firstResult.data;
      this.hasData = firstResult.data.length > 0;

      this.widgetComponent.updateWidgetActionComments();

      await this.actionPlanCacheService.waitUntilLoaded;
    } catch (error) {
      this.logger.error('Error executing impact report:', { error });
      this.hasData = false;
      this.tableDataSource.data = [];
    } finally {
      this.loadingFinished.emit(initialLoading);
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  public onOpenSaveDocumentsToCollection(
    destinationType: 'NEW' | 'EXISTING',
    explicitPointName: string
  ): void {
    this.widgetComponent.openSaveDocumentsToCollection(
      destinationType,
      'REPORT',
      this.filterService.getQueryForDocuments(
        this.getWidgetQueryOptions(this.columnFilterType, explicitPointName)
      ),
      explicitPointName
    );
  }

  public setSelectedColumnFilter(
    label: string,
    labelDisplay: string,
    columnFilterType?: 'left' | 'right'
  ): void {
    this.pointName = label;
    this.pointDisplayName = labelDisplay;
    this.columnFilterType = columnFilterType;
  }

  getCellMenuData(row: any, column: any, _extraData: any) {
    const value = this.columnFilterType && row[`${this.columnFilterType}Score`];

    return {
      item: this.pointName,
      label: row.label,
      labelDisplay: row.labelDisplay,
      column,
      value,
    };
  }

  openCellMenu($event: any, row: any, column: any, extraData: any): void {
    if (this.disablePinnedCommentClick) {
      return;
    }

    this.setSelectedColumnFilter(row.label, row.labelDisplay, extraData?.columnFilterType);

    super.openCellMenu($event, row, column, extraData);
  }

  onCellCommentIconClicked($event, row, column, extraData: any) {
    $event.stopPropagation();
    if (this.disablePinnedCommentClick) {
      return;
    }
    this.setSelectedColumnFilter(row.label, row.labelDisplay, extraData?.columnFilterType);

    super.onCellCommentIconClicked($event, row, column, extraData);
  }

  public isDataSourceChangeDisabled() {
    return this.payload.isSpecial;
  }

  public canAddToWidgetGroup() {
    return !this.payload.isSpecial;
  }

  public canAddToComparison(): boolean {
    return !this.payload.isSpecial;
  }

  public isWidgetGroupChangeDisabled() {
    return this.payload.isSpecial;
  }

  public onOpenSmartSummaryDialog(item: ImpactDataItem): void {
    this.setSelectedColumnFilter(item.label, item.labelDisplay);

    const previousStart: Date = new Date(this.widgetGroup.parameters.previousStart);
    const previousEnd: Date = new Date(this.widgetGroup.parameters.previousEnd);
    const currentStart: Date = new Date(this.widgetGroup.parameters.currentStart);
    const currentEnd: Date = new Date(this.widgetGroup.parameters.currentEnd);
    const dateRange: PredictiveDateRange = {
      previous: {
        first: previousStart,
        last: previousEnd,
      },
      current: {
        first: currentStart,
        last: currentEnd,
      },
    };

    this.dialogService.showDialog(
      SmartSummaryDialogComponent,
      {
        projectId: this.payload.projectId,
        collectionId: this.widgetDataSource.collectionName,
        analysisId: this.widgetDataSource.analysisId,
        driverAnalysisId: this.widgetGroup.parameters.driverAnalysisId,
        breakdownColumn: this.widgetGroup.parameters.breakdownColumn,
        breakdown: this.widgetGroup.parameters.selectedBreakdown,
        driver: this.widgetGroup.parameters.selectedDriver,
        dateRange,
        hasAnalyticItem: true,
        analyticItemType: this.payload.analyticItem,
        analyticItemName: item.label,
        analyticItemImpact:
          this.payload.sort.order === 'desc'
            ? AnalyticItemImpact.POSITIVE
            : AnalyticItemImpact.NEGATIVE,
        documentQuery:
          this.widgetDataSource.query ||
          this.filterService.getQueryForDocuments(this.getWidgetQueryOptions(null)),
      },
      {},
      false
    );
  }

  get isReadOnly() {
    return this.widgetComponent.isReadOnly;
  }

  get isExpansionPanelShowing() {
    return this.widgetComponent.isExpansionPanelShowing;
  }

  override hideOrShowAllComments(): void {
    this.cdr.markForCheck();
  }

  public canApplyComparison(comparison?: Comparison): boolean {
    if (this.payload.isSpecial || this.payload.type === 'focusAreas') {
      return false;
    }
    if (!comparison) {
      return true;
    }
    return comparison.filters.length < 3;
  }

  get widgetQueryOptions() {
    return null;
  }

  revokeComparison() {
    this.payload.sort.field = 'right';
  }

  public openSmartSummary(explicitPointName?: string) {
    super.openSmartSummary(explicitPointName ?? this.pointName);
  }

  onSmartSummaryClosed($event) {
    super.onSmartSummaryClosed($event);

    if ($event) {
      this.refresh();
    }
  }

  get isImpactMenuItemDisabled(): boolean {
    return this.isReadOnly || this.dashboard.locked;
  }

  onOpenActionPlanDialog(forItem: boolean): void {
    this.actionPlanDialogOptions = {
      forItem,
      actionPlan: this.actionPlanByRatingField,
      actionItems: this.actionItems,
      itemTag: this.pointName,
      widgetDataSource: this.widgetDataSource,
      dashboard: this.dashboard,
      payload: this.payload,
      widgetQueryOptions: this.getWidgetQueryOptions(this.columnFilterType),
      widgetId: this.widget.id,
      widgetGroup: this.widgetGroup,
      comparison: this.comparison,
    };

    this.isActionPlanDialogOpen = true;
  }

  onCloseActionPlanDialog(): void {
    this.isActionPlanDialogOpen = false;
  }

  onOpenImpactMenu(item: ImpactDataItem): void {
    this.focusAreaItem = item;
    this.pointName = item.label;
    this.pointDisplayName = item.labelDisplay;

    this.canCreateActionItem = !this.actionItems.find(
      (i) => i.item_value === this.pointName && i.item_type === this.payload.analyticItem
    );
    this.canCreateSmartSummary = this.pointHasRequiredDocCountForSmartSummaries(item);
    if (this.isSmartSummaryEnabled) {
      this.pointSummaries = this.getSmartSummariesForPoint(this.pointName);
      this.interactiveSmartSummaryMenu = this.buildSmartSummaryMenu();
      this.removeInteractiveSmartSummaryMenu = this.buildSmartSummaryRemoveMenu();
    }
    this.pointComments = this.getCommentsForPoint(item);

    this.setActionItemCreationTooltip();
  }

  private setActionPlanByRatingField(): void {
    this.actionPlanByRatingField = this.actionPlans.find(
      (a) => a.metadata.driverColumn === this.payload.ratingField
    );
  }

  get hasActionPlanByRatingField(): boolean {
    return !!this.actionPlanByRatingField;
  }

  private setActionItemCreationTooltip(): void {
    if (!this.hasActionPlanByRatingField) {
      this.actionItemCreationTooltip = translate('widgets.must-create-action-plan-first');
    } else if (!this.canCreateActionItem) {
      this.actionItemCreationTooltip = translate('widgets.action-item-already-exists');
    } else {
      this.actionItemCreationTooltip = undefined;
    }
  }

  private listActionItems(): void {
    this.setActionPlanByRatingField();

    if (this.hasActionPlanByRatingField) {
      this.actionItemCacheService.list(this.actionPlanByRatingField.plan_id);
    } else {
      this.actionItems = [];
    }
  }

  itemToCommentAnalyticItem() {
    return [
      {
        name: this.pointName,
        displayName: this.pointDisplayName,
        type: this.widget.payload.analyticItem,
        subType: this.widget.payload.entityTypeFilter,
      },
    ];
  }

  itemToCommentPoint(item: any): CommentPoint {
    const value = this.itemClicked?.value ?? 0;

    return {
      point: {
        x: value,
        y: value,
        xAxis: 0,
        yAxis: 0,
      },
      pointId: formatPointId(item, this.widget),
    };
  }

  cellToPoint(row?: string, column?: string) {
    return { item: row, column };
  }

  getCommentsForPoint(point: any) {
    return this.commentsByPointId().get(formatPointId(point, this.widget));
  }

  getCommentChangeForComment(comment: any) {
    if (!comment) {
      return;
    }

    return this.impactReport.getCommentDataChange(
      comment,
      this.widget,
      this.dashboard,
      this.tableDataSource.data
    );
  }

  getVisibleComments(): Comment[] {
    return super
      .getVisibleComments()
      .filter((c) =>
        this.impactReport?.isCommentVisible(c, this.widget, this.tableDataSource.data)
      );
  }

  focusAreaCommentClicked(event, area: ImpactDataItem) {
    event.stopImmediatePropagation();
    this.pointComments = this.getCommentsForPoint(area);
    this.onCommentListOpenClicked(area);
  }

  handleAreaClick(event, area: ImpactDataItem) {
    if (this.dashboard.locked || this.widgetComponent.isExpansionPanelShowing) {
      return;
    }
    const menuPositionCalculator = (e) => {
      const targetElem = event.target.closest('div.area-item');
      const scrollLeft = targetElem.offsetParent.parentElement.scrollLeft;
      const scrollTop = targetElem.offsetParent.parentElement.scrollTop;

      const left =
        e.offsetX + targetElem.offsetLeft + targetElem.offsetParent.offsetLeft - scrollLeft;
      const top =
        event.offsetY + targetElem.offsetTop + targetElem.offsetParent.offsetTop - scrollTop;

      return { left, top };
    };

    if (!event.target.className.includes('comment')) {
      const { top, left } = menuPositionCalculator(event);
      this.menuTriggerNativeElement.nativeElement.setAttribute(
        'style',
        `left: ${left}px; top: ${top}px`
      );

      this.menuTriggerElement.menuData = area;
      this.onOpenImpactMenu(area);
      this.menuTriggerElement.openMenu();
    }
  }

  getAreaComments(area: ImpactDataItem): boolean {
    if (this.widget.payload.commentsHidden) {
      return false;
    }

    return this.widget.payload.comments.some((c) => c.target.pointId === area.label);
  }

  private refreshDataSourceToRedrawIcons() {
    if (this.widget.payload.type === 'focusAreas') {
      this.tableDataSource.data = [...this.tableDataSource.data.map((item) => ({ ...item }))];
    }
  }

  addComment(comment: Comment) {
    super.addComment(comment);
    this.refreshDataSourceToRedrawIcons();
  }

  editComment(comment: Comment): void {
    super.editComment(comment);
    this.refreshDataSourceToRedrawIcons();
  }

  deleteComment(comment: Comment) {
    super.deleteComment(comment);
    this.refreshDataSourceToRedrawIcons();
  }

  handleMaxWidthChange(newWidthWithPixels: string | null) {
    this.ratingModalWidth = newWidthWithPixels ?? this.DEFAULT_RATING_MODAL_WIDTH;
  }

  override getAiInsightDataPointCount(_handler: MatchmakerHandler): number {
    return this.tableDataSource.data.length;
  }

  override getAiInsightEligiblePointCount(): number {
    return this.tableDataSource.data.filter(
      (d) => d.documentCount >= Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT
    ).length;
  }

  override autoSelectAiInsightPoints(_handler: MatchmakerHandler) {
    const data = this.tableDataSource.data[0];

    this.onInsightPointSelection(data, 'right');
  }

  onInsightPointSelection(row: ImpactDataItem, column, _extraData?) {
    if (
      column === 'label' ||
      isNaN(row[`${column}Score`]) ||
      row.selectionState?.[column]?.selected
    ) {
      return;
    }

    if (
      this.insightPointSelectionState.pointSelectionRule === 'ALL_SAME_GROUP' &&
      (row.selectionState?.left?.selected || row.selectionState?.right?.selected)
    ) {
      return;
    }

    if (row.documentCount < Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT) {
      this.alertService.showToast(
        translate('widgets.ai-insights.doc-threshold.below-min-snackbar', {
          count: Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT,
        })
      );
      return;
    }

    row.selectionState ??= {};
    row.selectionState[column] = {
      selected: true,
      index: ++this.insightPointSelectionState.pointCount,
    };

    const selection = [this.createInsightPoint(row, column)];

    if (this.insightPointSelectionState.isApplyComparison && this.comparison?.filters.length > 1) {
      const otherColumn = column === 'left' ? 'right' : 'left';
      const comparisonPoint = this.createInsightPoint(row, otherColumn);

      selection.push(comparisonPoint);
      if (column === 'left') {
        selection.reverse();
      }
    }

    this.aiInsightPointSelection.next([
      {
        point: selection,
        isSelected: true,
      },
    ]);
  }

  override endAiInsightPointSelectionFlow() {
    this.tableDataSource.data.forEach((d) => delete d.selectionState);

    super.endAiInsightPointSelectionFlow();
  }

  private createInsightPoint(dataPoint: ImpactDataItem, column: 'left' | 'right'): any {
    const comparisonFilterIdx = column === 'right' ? 0 : 1;
    return {
      documentCount: dataPoint.documentCount,
      y: dataPoint[`${column}Score`],
      pointId: dataPoint.labelDisplay,
      series: {
        index: comparisonFilterIdx,
        name: this.comparison?.filters[comparisonFilterIdx]?.label,
      } as any,
    };
  }
}
